import React from 'react'
import { map, get, isObject, upperFirst, replace, camelCase, isEmpty } from 'lodash'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import FormikTextField from './FormikTextField'
import FormikSelectField from './FormikSelectField'
import FormikCheckboxField from './FormikCheckboxField'
import FormikNumericField from './FormikNumericField'
import FormikPasswordField from './FormikPasswordField'
import FormikArrayField from './FormikArrayField'
import PageSection from '../layouts/PageSection'
import { evaluateOnlyConditions } from '../../utils/schemaFormUtils'

const formatLabel = (name) => {
  return upperFirst(replace(name, /_/g, ' '))
}

const SchemaField = ({ formik, field, path, nodes, domains, ingressControllers, networks }) => {
  const fieldName = camelCase(field.name)
  const name = path ? `${path}.${fieldName}` : fieldName
  const label = field.label || formatLabel(field.name)
  const isRequired = field.required

  // Check conditional visibility
  if (field.only && !isEmpty(field.only)) {
    const shouldShow = evaluateOnlyConditions(field.only, formik.values)
    if (!shouldShow) {
      return null
    }
  }

  // Handle password fields
  if (field.format === 'password') {
    return (
      <Box>
        <FormikPasswordField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          helperText={field.description}
          passwordPolicy={field.passwordPolicy}
        />
      </Box>
    )
  }

  if (field.type === 'boolean') {
    return (
      <Box>
        <FormikCheckboxField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          helperText={field.description}
        />
      </Box>
    )
  }

  if (field.type === 'enum' && field.options) {
    const options = map(field.options, (value) => ({
      value,
      label: value
    }))

    return (
      <Box>
        <FormikSelectField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          options={options}
          helperText={field.description}
          multiple={field.multiple}
        />
      </Box>
    )
  }

  // Handle node selector (supports multiple)
  if (field.type === 'node') {
    const nodeOptions = map(nodes, (node) => ({
      value: node.id,
      label: node.name || node.ip
    }))

    return (
      <Box>
        <FormikSelectField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          options={nodeOptions}
          helperText={field.description}
          multiple={field.multiple}
        />
      </Box>
    )
  }

  // Handle domain selector (supports multiple)
  if (field.type === 'domain') {
    const domainOptions = map(domains, (domain) => ({
      value: domain.id,
      label: domain.domain
    }))

    return (
      <Box>
        <FormikSelectField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          options={domainOptions}
          helperText={field.description}
          multiple={field.multiple}
        />
      </Box>
    )
  }

  // Handle ingress controller selector (supports multiple)
  if (field.type === 'ingress_controller') {
    const ingressControllerOptions = map(ingressControllers, (ic) => ({
      value: ic.id,
      label: ic.name
    }))

    return (
      <Box>
        <FormikSelectField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          options={ingressControllerOptions}
          helperText={field.description}
          multiple={field.multiple}
        />
      </Box>
    )
  }

  // Handle network selector (supports multiple)
  if (field.type === 'network') {
    const networkOptions = map(networks, (network) => ({
      value: network.id,
      label: network.name
    }))

    return (
      <Box>
        <FormikSelectField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          options={networkOptions}
          helperText={field.description}
          multiple={field.multiple}
        />
      </Box>
    )
  }

  // Handle multiple/array fields (for generic string arrays without specific type)
  if (field.multiple) {
    return (
      <Box>
        <FormikArrayField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          addLabel={`Add ${formatLabel(field.name)}`}
          placeholder={field.description}
        />
      </Box>
    )
  }

  if (field.type === 'integer' || field.type === 'number') {
    return (
      <Box>
        <FormikNumericField
          formik={formik}
          name={name}
          label={isRequired ? `${label} *` : label}
          fractionDigits={field.type === 'integer' ? 0 : 2}
          helperText={field.description}
        />
      </Box>
    )
  }

  // Default to text field for strings, email, and other types
  return (
    <Box>
      <FormikTextField
        formik={formik}
        name={name}
        label={isRequired ? `${label} *` : label}
        type={field.type === 'email' ? 'email' : 'text'}
        placeholder={field.default}
        helperText={field.description}
      />
    </Box>
  )
}

const FormikSchemaFields = ({ formik, schema, nodes = [], domains = [], ingressControllers = [], networks = [] }) => {
  if (!schema || !isObject(schema)) {
    return null
  }

  const groups = get(schema, 'groups', [])

  return (
    <>
      {map(groups, (group) => (
        <Box key={group.name} sx={{ mb: 4 }}>
          {/* Group as full-width heading */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3">{group.title}</Typography>
            {group.description && (
              <Typography variant="body2" color="text.secondary">{group.description}</Typography>
            )}
          </Box>

          {/* Segments use PageSection (left-handed layout) */}
          {map(get(group, 'segments', []), (segment) => (
            <PageSection key={segment.name} title={segment.title} subtitle={segment.description}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {map(get(segment, 'fields', []), (field) => (
                  <SchemaField
                    key={field.name}
                    formik={formik}
                    field={field}
                    path={`${camelCase(group.name)}.${camelCase(segment.name)}`}
                    nodes={nodes}
                    domains={domains}
                    ingressControllers={ingressControllers}
                    networks={networks}
                  />
                ))}
              </Box>
            </PageSection>
          ))}
        </Box>
      ))}
    </>
  )
}

export default FormikSchemaFields
